import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmptyRow,
  DataTableHead,
  DataTableHeadCell,
  DataTableHeaderRow,
  DataTableRow,
  DataTableScroll,
  DataTableShell,
} from "@/components/ui/DataTable";
import { useAdminDiagnostics } from "../hooks/useAdminData";
import type { AdminDiagnostics } from "@/lib/types";
import { AdminPageHeader } from "../components/AdminPageHeader";

/**
 * `/admin/overview`.
 *
 * MVP shape: one club, many teams. The Overview is a club-operations
 * dashboard: usage, team momentum, and intervention points. Detailed
 * storage/encoder inspection stays in Files / Live / Performance.
 */
export function OverviewPage() {
  const diag = useAdminDiagnostics();
  const d = diag.data;
  const club = d?.club;
  const totals = club?.totals;
  const status = deriveClubStatus(d);

  return (
    <div data-testid="admin-overview" className="flex flex-col gap-4">
      <AdminPageHeader
        eyebrow="Club Operations"
        title="Club Overview"
        description="Track whether the club is using Tktic, which teams are moving, and where an operator should step in."
        meta={diag.isFetching ? "Refreshing..." : "Auto-refreshes every 10 s"}
      />

      {diag.error ? (
        <p
          data-testid="admin-overview-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load admin diagnostics.
        </p>
      ) : null}

      <StatusBand payload={d} status={status} />

      <div
        data-testid="admin-overview-kpis"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
      >
        <MetricTile
          testid="kpi-teams"
          label="Active Teams"
          value={totals ? String(totals.teams) : "-"}
          sub={totals ? `${totals.public_teams} public` : ""}
        />
        <MetricTile
          testid="kpi-matches"
          label="Matches This Month"
          value={totals ? String(totals.matches_last_30d) : "-"}
          sub={totals ? `${totals.matches} total library matches` : ""}
        />
        <MetricTile
          testid="kpi-feedback"
          label="Feedback This Month"
          value={totals ? String(totals.feedback_last_30d) : "-"}
          sub={totals ? `${totals.feedback_items} notes, clips, recaps` : ""}
        />
        <MetricTile
          testid="kpi-account-coverage"
          label="Account Coverage"
          value={totals ? `${totals.linked_player_pct}%` : "-"}
          sub={
            totals
              ? `${totals.linked_players} of ${totals.active_players} active players linked`
              : ""
          }
          tone={coverageTone(totals?.linked_player_pct)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.75fr)]">
        <TeamMomentum teams={club?.team_metrics ?? []} />
        <NeedsAttention payload={d} />
      </div>

      <SystemSnapshot payload={d} />
    </div>
  );
}

type Tone = "good" | "warn" | "bad" | "busy" | "idle" | "muted";
type ClubStatus = "healthy" | "attention" | "critical" | "loading";

const TONE_CLASSES: Record<Tone, string> = {
  good: "text-good",
  warn: "text-accent",
  bad: "text-destructive",
  busy: "text-accent",
  idle: "text-ink-muted",
  muted: "text-ink",
};

/** Fail-safe tone for disk-dependent admin surfaces (#212). */
export function diskTone(
  disk: AdminDiagnostics["disk"] | undefined,
): Tone {
  if (!disk) return "muted";
  const free = disk.free;
  const min = disk.min_free_bytes;
  if (typeof free !== "number" || !Number.isFinite(free)) return "bad";
  if (free <= (typeof min === "number" ? min : 0)) return "bad";
  return disk.enough_space ? "good" : "warn";
}

export function deriveClubStatus(
  payload: AdminDiagnostics | undefined,
): ClubStatus {
  if (!payload) return "loading";
  const attention = payload.club?.attention ?? [];
  if (diskTone(payload.disk) === "bad") return "critical";
  if (
    attention.some((item) => item.severity === "critical") ||
    (payload.counts.failed_slots ?? 0) > 0
  ) {
    return "critical";
  }
  if (
    attention.some((item) => item.severity === "warn") ||
    diskTone(payload.disk) === "warn" ||
    (payload.counts.hls_missing_slots ?? 0) > 0
  ) {
    return "attention";
  }
  return "healthy";
}

function StatusBand({
  payload,
  status,
}: {
  payload: AdminDiagnostics | undefined;
  status: ClubStatus;
}): ReactNode {
  const copy: Record<ClubStatus, { label: string; tone: Tone; detail: string }> = {
    loading: {
      label: "Loading",
      tone: "muted",
      detail: "Reading the current club and system state.",
    },
    healthy: {
      label: "Healthy",
      tone: "good",
      detail: "No club or video pipeline blockers need attention.",
    },
    attention: {
      label: "Needs attention",
      tone: "warn",
      detail: "Usage or media health has items worth checking.",
    },
    critical: {
      label: "Critical",
      tone: "bad",
      detail: "A storage or video pipeline issue may block users.",
    },
  };
  const item = copy[status];
  const attentionCount = payload?.club?.attention.length ?? 0;
  return (
    <section
      data-testid="admin-overview-status"
      data-status={status}
      className="rounded border border-rule bg-surface shadow-sm"
    >
      <div className="grid gap-3 p-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusDot tone={item.tone} />
            <p className="font-display text-sm uppercase tracking-wider text-ink">
              {item.label}
            </p>
            <span className="rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] text-ink-muted">
              {attentionCount} action{attentionCount === 1 ? "" : "s"}
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-muted">{item.detail}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 md:flex md:items-center">
          <MiniSignal
            label="Disk"
            value={payload ? formatBytes(payload.disk.free) : "-"}
            tone={diskTone(payload?.disk)}
          />
          <MiniSignal
            label="Failed"
            value={payload ? String(payload.counts.failed_slots) : "-"}
            tone={(payload?.counts.failed_slots ?? 0) > 0 ? "bad" : "good"}
          />
          <MiniSignal
            label="Uploads"
            value={payload ? String(activeUploadCount(payload)) : "-"}
            tone={activeUploadCount(payload) > 0 ? "busy" : "idle"}
          />
          <MiniSignal
            label="HLS"
            value={payload ? String(payload.counts.hls_missing_slots) : "-"}
            tone={(payload?.counts.hls_missing_slots ?? 0) > 0 ? "warn" : "good"}
          />
        </div>
      </div>
    </section>
  );
}

function MetricTile({
  label,
  value,
  sub,
  tone = "muted",
  testid,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  testid: string;
}): ReactNode {
  return (
    <div
      data-testid={testid}
      data-tone={tone}
      className="rounded border border-rule bg-surface p-3 shadow-sm"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <div className={`mt-2 font-mono text-2xl leading-none ${TONE_CLASSES[tone]}`}>
        {value}
      </div>
      {sub ? <p className="mt-2 text-xs text-ink-muted">{sub}</p> : null}
    </div>
  );
}

function TeamMomentum({
  teams,
}: {
  teams: NonNullable<AdminDiagnostics["club"]>["team_metrics"];
}): ReactNode {
  return (
    <section data-testid="admin-overview-teams" className="flex flex-col gap-2">
      <SectionTitle
        title="Team Momentum"
        detail="Which teams are building a match and feedback rhythm."
      />
      <DataTableShell className="hidden md:block">
        <DataTableScroll>
          <DataTable>
            <DataTableHead>
              <DataTableHeaderRow>
                <DataTableHeadCell>Team</DataTableHeadCell>
                <DataTableHeadCell align="right">Matches</DataTableHeadCell>
                <DataTableHeadCell align="right">Feedback</DataTableHeadCell>
                <DataTableHeadCell align="right">Linked</DataTableHeadCell>
                <DataTableHeadCell>Last Match</DataTableHeadCell>
                <DataTableHeadCell align="right">Attention</DataTableHeadCell>
              </DataTableHeaderRow>
            </DataTableHead>
            <DataTableBody>
              {teams.length === 0 ? (
                <DataTableEmptyRow colSpan={6}>
                  No teams have been created yet.
                </DataTableEmptyRow>
              ) : (
                teams.map((team) => (
                  <DataTableRow key={team.team_id}>
                    <DataTableCell>
                      <div className="flex min-w-0 flex-col">
                        <span className="font-display text-sm text-ink">
                          {team.team_name}
                        </span>
                        <span className="font-mono text-[0.65rem] text-ink-muted">
                          {team.game_format ?? "-"} {team.public ? "public" : "private"}
                        </span>
                      </div>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <MetricPair main={team.matches} sub={`+${team.matches_last_30d}/30d`} />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <MetricPair main={team.feedback_items} sub={`+${team.feedback_last_30d}/30d`} />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <MetricPair
                        main={`${team.linked_player_pct}%`}
                        sub={`${team.linked_players}/${team.active_players}`}
                        tone={coverageTone(team.linked_player_pct)}
                      />
                    </DataTableCell>
                    <DataTableCell>
                      <span className="block text-xs text-ink">
                        {formatDate(team.last_match_date)}
                      </span>
                      <span className="block max-w-48 truncate text-[0.65rem] text-ink-muted">
                        {team.last_match_label ?? "No match"}
                      </span>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <span
                        className={`font-mono text-sm ${
                          team.needs_attention > 0 ? "text-accent" : "text-good"
                        }`}
                      >
                        {team.needs_attention}
                      </span>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </DataTableBody>
          </DataTable>
        </DataTableScroll>
      </DataTableShell>
      <div className="grid gap-2 md:hidden">
        {teams.length === 0 ? (
          <div className="rounded border border-rule bg-surface p-3 text-sm text-ink-muted shadow-sm">
            No teams have been created yet.
          </div>
        ) : (
          teams.map((team) => (
            <article
              key={team.team_id}
              className="rounded border border-rule bg-surface p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate font-display text-sm text-ink">
                    {team.team_name}
                  </h3>
                  <p className="font-mono text-[0.65rem] text-ink-muted">
                    {team.game_format ?? "-"} · {team.public ? "public" : "private"}
                  </p>
                </div>
                <span className="font-mono text-sm text-accent">
                  {team.needs_attention} flags
                </span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <MobileMetric label="Matches" value={team.matches} sub={`+${team.matches_last_30d}/30d`} />
                <MobileMetric label="Feedback" value={team.feedback_items} sub={`+${team.feedback_last_30d}/30d`} />
                <MobileMetric label="Linked" value={`${team.linked_player_pct}%`} sub={`${team.linked_players}/${team.active_players}`} />
              </div>
              <p className="mt-3 truncate text-xs text-ink-muted">
                Last: {formatDate(team.last_match_date)} · {team.last_match_label ?? "No match"}
              </p>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function NeedsAttention({
  payload,
}: {
  payload: AdminDiagnostics | undefined;
}): ReactNode {
  const items = payload?.club?.attention ?? [];
  return (
    <section
      data-testid="admin-overview-attention"
      className="flex flex-col gap-2"
    >
      <SectionTitle
        title="Needs Attention"
        detail="Actionable club or media issues, not raw audit noise."
      />
      <div className="rounded border border-rule bg-surface shadow-sm">
        {items.length === 0 ? (
          <p className="p-3 text-sm text-ink-muted">
            Nothing needs operator attention right now.
          </p>
        ) : (
          <ul className="divide-y divide-rule/60">
            {items.slice(0, 8).map((item, index) => (
              <li
                key={`${item.kind}-${item.team_id ?? "club"}-${index}`}
                data-severity={item.severity}
                className="p-3"
              >
                <div className="flex items-start gap-2">
                  <StatusDot tone={attentionTone(item.severity)} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">{item.title}</p>
                    <p className="mt-1 text-xs text-ink-muted">{item.detail}</p>
                    {item.href ? (
                      <Link
                        to={item.href}
                        className="mt-2 inline-flex rounded border border-rule px-2 py-1 font-display text-[0.6rem] uppercase tracking-wider text-ink-muted transition-colors hover:border-accent hover:text-accent"
                      >
                        Open
                      </Link>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function SystemSnapshot({
  payload,
}: {
  payload: AdminDiagnostics | undefined;
}): ReactNode {
  return (
    <section data-testid="admin-overview-system" className="flex flex-col gap-2">
      <SectionTitle
        title="System Snapshot"
        detail="Compact health checks. Deep controls live in Files, Live, and Performance."
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          testid="kpi-disk"
          label="Free Disk"
          value={payload ? formatBytes(payload.disk.free) : "-"}
          sub={payload ? `minimum ${formatBytes(payload.disk.min_free_bytes)}` : ""}
          tone={diskTone(payload?.disk)}
        />
        <MetricTile
          testid="kpi-failed"
          label="Failed Slots"
          value={payload ? String(payload.counts.failed_slots) : "-"}
          sub={payload ? `${payload.recent_errors.length} recent errors` : ""}
          tone={(payload?.counts.failed_slots ?? 0) > 0 ? "bad" : "good"}
        />
        <MetricTile
          testid="kpi-hls-backfill"
          label="HLS Backfill"
          value={payload?.hls.backfill_running ? "Running" : "Idle"}
          sub={payload ? `${payload.counts.hls_missing_slots} slots missing HLS` : ""}
          tone={payload?.hls.backfill_running ? "busy" : "idle"}
        />
        <MetricTile
          testid="kpi-chunk"
          label="Upload Session"
          value={payload ? formatBytes(payload.upload_limits.chunk_size_bytes) : "-"}
          sub={
            payload
              ? `${payload.upload_limits.stale_upload_session_seconds}s stale timeout`
              : ""
          }
        />
      </div>
    </section>
  );
}

function SectionTitle({
  title,
  detail,
}: {
  title: string;
  detail: string;
}): ReactNode {
  return (
    <header>
      <h3 className="font-display text-sm uppercase tracking-wider text-ink">
        {title}
      </h3>
      <p className="text-xs text-ink-muted">{detail}</p>
    </header>
  );
}

function MiniSignal({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone: Tone;
}): ReactNode {
  return (
    <div className="rounded border border-rule bg-elevated px-2 py-1.5">
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`font-mono text-xs ${TONE_CLASSES[tone]}`}>{value}</span>
    </div>
  );
}

function MetricPair({
  main,
  sub,
  tone = "muted",
}: {
  main: ReactNode;
  sub: ReactNode;
  tone?: Tone;
}): ReactNode {
  return (
    <span className="inline-flex flex-col items-end">
      <span className={`font-mono text-sm ${TONE_CLASSES[tone]}`}>{main}</span>
      <span className="font-mono text-[0.65rem] text-ink-muted">{sub}</span>
    </span>
  );
}

function MobileMetric({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub: ReactNode;
}): ReactNode {
  return (
    <div className="rounded border border-rule bg-elevated px-2 py-1.5">
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="block font-mono text-sm text-ink">{value}</span>
      <span className="block font-mono text-[0.65rem] text-ink-muted">{sub}</span>
    </div>
  );
}

function StatusDot({ tone }: { tone: Tone }): ReactNode {
  const classes: Record<Tone, string> = {
    good: "bg-good",
    warn: "bg-accent",
    bad: "bg-destructive",
    busy: "bg-accent",
    idle: "bg-ink-muted",
    muted: "bg-ink-muted",
  };
  return (
    <span
      aria-hidden="true"
      className={`mt-1 h-2 w-2 shrink-0 rounded-full ${classes[tone]}`}
    />
  );
}

function activeUploadCount(payload: AdminDiagnostics | undefined): number {
  return (payload?.upload_sessions ?? []).filter((session) => session.status === "active").length;
}

function coverageTone(value: number | undefined): Tone {
  if (value === undefined) return "muted";
  if (value >= 80) return "good";
  if (value >= 60) return "warn";
  return "bad";
}

function attentionTone(severity: "critical" | "warn" | "info"): Tone {
  if (severity === "critical") return "bad";
  if (severity === "warn") return "warn";
  return "muted";
}

/** Compact byte formatter — "12.3 GB" / "256 MB" / "4 KB". */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.length > 10 ? value.slice(0, 10) : value;
  }
  return date.toISOString().slice(0, 10);
}
